import React, { useEffect, useState } from "react";
import { Check, CreditCard, ExternalLink, Loader2, Minus, Plus, TriangleAlert } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const UsageBar = ({ label, used, cap }) => {
  const unlimited = cap === null || cap === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(cap, 1)) * 100));
  const tone = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div data-testid={`usage-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="tabular-nums text-slate-500">
          {used}{unlimited ? " · unlimited" : ` / ${cap}`}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-slate-100">
          <div className={`h-full rounded ${tone} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [interval, setIntervalKey] = useState("monthly");
  const [seats, setSeats] = useState(5);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/billing");
      setData(d);
      setSeats(Math.max(d.usage.users || 1, d.current.seats || 5));
      if (d.current.billing_interval) setIntervalKey(d.current.billing_interval);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load billing");
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data: res } = await api.post("/billing/portal", { return_url: window.location.origin });
      window.location.href = res.portal_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      if (current.subscribed) {
        // Changing an existing subscription must never create a second one.
        await api.post("/billing/change", { plan, interval, seats });
        await load();
        toast.success("Your subscription has been updated");
        setBusy(null);
        return;
      }
      const { data: res } = await api.post("/billing/checkout", {
        plan, interval, seats, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  if (!data) return <Shell title="Billing"><Empty testId="billing-loading" text="Loading…" /></Shell>;

  const { catalogue, current, usage, limits } = data;
  const annual = interval === "annual";

  return (
    <Shell
      testId="admin-billing-page"
      title="Plan and Billing"
      subtitle="Your current plan, what you are using it for, and how to change it. Prices exclude VAT."
    >
      <div className="space-y-6">
        <div className="panel p-5" data-testid="billing-current">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="overline-label">Current plan</span>
              <p className="font-display text-2xl font-semibold text-slate-900">
                {catalogue.plans.find((p) => p.key === current.effective_plan)?.name || current.effective_plan}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {current.status === "licensed"
                  ? "Licensed workspace — full access with no subscription or billing required."
                  : current.trial
                  ? `Trial — ${current.days_remaining} day${current.days_remaining === 1 ? "" : "s"} of full Professional access remaining.`
                  : current.subscribed
                    ? `${current.seats} seat${current.seats === 1 ? "" : "s"}, billed ${current.billing_interval === "annual" ? "annually" : "monthly"}.`
                    : "No subscription yet."}
              </p>
            </div>
            {current.read_only && (
              <p className="flex items-center gap-2 rounded-sm bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800">
                <TriangleAlert size={14} /> This workspace is read-only. Choose a plan to start editing again.
              </p>
            )}
            {current.subscribed && (
              <Button
                variant="outline"
                data-testid="billing-portal-btn"
                disabled={busy === "portal"}
                onClick={openPortal}
                className="gap-1.5"
              >
                {busy === "portal" ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                Manage billing
              </Button>
            )}
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <UsageBar label="Users" used={usage.users} cap={limits.users} />
            <UsageBar label="Projects" used={usage.projects} cap={limits.projects} />
            <UsageBar label="Programmes" used={usage.programmes} cap={limits.programmes} />
            <UsageBar label="Portfolios" used={usage.portfolios} cap={limits.portfolios} />
          </div>
        </div>

        {current.status !== "licensed" && (
        <div className="panel p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">Choose a plan</h3>
              <p className="mt-1 text-sm text-slate-600">
                Seats can be changed later. Annual billing is charged for twelve months up front at the lower rate.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Seats</span>
                <div className="flex items-center rounded-sm border border-slate-200">
                  <button data-testid="seats-minus" className="px-2 py-1.5 text-slate-500 hover:text-slate-900"
                    onClick={() => setSeats((s) => Math.max(usage.users || 1, s - 1))}>
                    <Minus size={13} />
                  </button>
                  <span data-testid="seats-value" className="w-10 text-center text-sm font-semibold tabular-nums">{seats}</span>
                  <button data-testid="seats-plus" className="px-2 py-1.5 text-slate-500 hover:text-slate-900"
                    onClick={() => setSeats((s) => Math.min(500, s + 1))}>
                    <Plus size={13} />
                  </button>
                </div>
              </div>
              <div className="flex rounded-sm border border-slate-200 p-0.5">
                {[["monthly", "Monthly"], ["annual", "Annual"]].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`interval-${key}`}
                    onClick={() => setIntervalKey(key)}
                    className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${
                      interval === key ? "bg-slate-900 text-white" : "text-slate-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {catalogue.plans.map((plan) => {
              const price = annual ? plan.price_annual : plan.price_monthly;
              const isCurrent = current.plan === plan.key && current.subscribed;
              return (
                <div
                  key={plan.key}
                  data-testid={`billing-plan-${plan.key}`}
                  className={`flex flex-col rounded-lg border p-5 ${
                    plan.recommended ? "border-[color:var(--brand-primary)] ring-1 ring-[color:var(--brand-primary)]" : "border-slate-200"
                  }`}
                >
                  <p className="font-display text-lg font-semibold text-slate-900">{plan.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{plan.tagline}</p>
                  <p className="mt-4">
                    {plan.quote_only ? (
                      <span className="font-display text-2xl font-semibold text-slate-900">POA</span>
                    ) : (
                      <>
                        <span className="font-display text-3xl font-semibold text-slate-900">£{price}</span>
                        <span className="text-xs text-slate-500"> per user / month</span>
                      </>
                    )}
                  </p>
                  {!plan.quote_only && (
                    <p className="mt-1 text-xs text-slate-500">
                      {seats} seat{seats === 1 ? "" : "s"} · £{price * seats * (annual ? 12 : 1)}{" "}
                      {annual ? "per year" : "per month"} + VAT
                    </p>
                  )}
                  <ul className="mt-4 flex-1 space-y-1.5">
                    {plan.headline_points.slice(0, 5).map((point) => (
                      <li key={point} className="flex gap-2 text-xs text-slate-600">
                        <Check size={12} className="mt-0.5 shrink-0 text-[color:var(--brand-primary)]" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  {plan.quote_only ? (
                    <Button variant="outline" className="mt-5 w-full" data-testid="billing-enterprise-btn"
                      onClick={() => { window.location.href = "/pricing#enterprise"; }}>
                      Talk to us
                    </Button>
                  ) : (
                    <Button
                      data-testid={`billing-checkout-${plan.key}`}
                      disabled={!!busy || isCurrent}
                      onClick={() => checkout(plan.key)}
                      className="mt-5 w-full gap-1.5"
                      style={{ backgroundColor: "var(--brand-primary)" }}
                    >
                      {busy === plan.key ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                      {isCurrent ? "Current plan" : current.subscribed ? "Switch to this plan" : "Subscribe"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Payments are processed by Stripe. Card details are entered with Stripe and never reach our servers.
            Minimum seats apply per plan and we will never reduce you below your current user count.
          </p>
        </div>
        )}
      </div>
    </Shell>
  );
}
